import React, { useState, useEffect, useRef } from 'react';
import { FaFileUpload, FaFolder, FaFile, FaTrash } from 'react-icons/fa';
import { uploadFile, getFiles, deleteFile } from '../services/driveService';

const Drive = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const fetchedFiles = await getFiles();
      setFiles(fetchedFiles);
    } catch (error) {
      setError('Failed to load files');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setUploading(true);
      setError('');
      await uploadFile(file);
      await loadFiles();
    } catch (error) {
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await deleteFile(fileId);
      await loadFiles();
    } catch (error) {
      setError('Failed to delete file');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">My Drive</h1>

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 mb-6 text-center
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${uploading ? 'opacity-50' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <FaFileUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600 mb-2">
          Drag and drop files here, or
          <button
            onClick={() => fileInputRef.current.click()}
            className="text-blue-500 hover:text-blue-600 ml-1"
          >
            browse
          </button>
        </p>
        <p className="text-sm text-gray-500">
          Supported files: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
        </p>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Files List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {files.map((file) => (
          <div
            key={file.id}
            className="bg-white rounded-lg shadow p-4 flex items-start space-x-3"
          >
            {file.type.includes('image') ? (
              <img
                src={file.url}
                alt={file.name}
                className="w-10 h-10 object-cover rounded"
              />
            ) : (
              <FaFile className="w-10 h-10 text-gray-400" />
            )}
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {file.name}
              </p>
              <p className="text-sm text-gray-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>

            <button
              onClick={() => handleDelete(file.id)}
              className="text-red-500 hover:text-red-700"
            >
              <FaTrash />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Drive;
